import { Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { WorkflowStep, WorkflowTemplate } from '@/api/workflows';
import { BmoRadioGroup } from '@/components/BmoRadioGroup';

import { collectPdfDocs } from './pdfPreviewActions';

const { Title, Paragraph, Text } = Typography;

const PILL_BUTTON: React.CSSProperties = {
  borderRadius: 999,
  minWidth: 120,
  height: 44,
  fontWeight: 600,
};

type Props = {
  template: WorkflowTemplate;
  step: WorkflowStep;
  /** Advisor's UI language — drives THIS card's own copy (title /
   *  description / bullets). Follows the AppShell header switcher via
   *  i18n.language upstream; the card is the advisor-facing side of this
   *  step so its copy reads in the advisor's locale, not the client's
   *  chosen PDF language. */
  language: 'en' | 'fr';
  /** Client's chosen PDF language — bound to the radio's `value` so the
   *  selection survives the per-step remount when the advisor navigates
   *  back here. */
  pdfLanguage: 'en' | 'fr';
  /** Radio change — updates FormShell's `pdfLanguage` state ONLY. Does NOT
   *  touch the advisor's UI locale; that stays where the AppShell header
   *  put it. Radio is disabled when the mount point wired no handler (a locked
   *  application), matching how `onCancel` already works. */
  onPdfLanguageChange?: (pdfLanguage: 'en' | 'fr') => void;
  /** CONTINUE — advances to the next step (same as the nav row's Next). Button hidden when the
   *  mount point wired no handler: a locked application has nothing to advance into. */
  onContinue?: () => void;
  /** CANCEL — exits the flow. Button hidden when the mount point wired no exit. */
  onCancel?: () => void;
};

/**
 * Welcome landing card — the first thing the advisor sees. Bold title,
 * description paragraph, subheader over the free-text PDF bullet list, an
 * optional English/French radio that gates the whole form's language, and
 * CANCEL / CONTINUE pill actions (the shared nav row is hidden on this step).
 *
 * Content comes from `step.welcomeConfig`; an unconfigured step falls back to
 * the step's own bilingual title/description, the fixed i18n list heading, and
 * the live PDF entry labels — the same defaults the config modal seeds from.
 */
export function WelcomeStep({
  template,
  step,
  language,
  pdfLanguage,
  onPdfLanguageChange,
  onContinue,
  onCancel,
}: Props) {
  // Chrome strings (Select a language / English / French / Cancel /
  // Continue) follow the normal app locale via useTranslation now that
  // FormShell no longer over-scopes a fixed-language `t` — the shell reads
  // straight off i18n.language.
  const { t } = useTranslation();
  const isFr = language === 'fr';
  const cfg = step.welcomeConfig;

  const pick = (en: string | undefined, fr: string | undefined): string =>
    ((isFr ? fr : en) || en || fr || '').trim();

  const title = pick(cfg?.titleEn, cfg?.titleFr) || pick(step.titleEn, step.titleFr);
  const description =
    pick(cfg?.descriptionEn, cfg?.descriptionFr) || pick(step.descriptionEn, step.descriptionFr);
  const subheader = cfg
    ? pick(cfg.subheaderEn, cfg.subheaderFr)
    : t('formRenderer.welcome.pdfHeading');

  // Saved bullet lines for the active language (cross-language fallback so a
  // single-locale author never shows an empty list); unconfigured steps derive
  // the list from the workflow's live PDF entries — same source the PDF
  // Generation step renders.
  const savedLines = isFr ? cfg?.pdfListFr : cfg?.pdfListEn;
  const fallbackLines = (isFr ? cfg?.pdfListEn : cfg?.pdfListFr) ?? [];
  const bullets = cfg
    ? savedLines?.length
      ? savedLines
      : fallbackLines
    : collectPdfDocs(template, language).map((d) => d.label);

  const showLanguageSelector = cfg?.showLanguageSelector !== false;

  return (
    // Full width — no max-width cap. The 900px cap this used to carry made
    // the Welcome card noticeably narrower than every other step's canvas,
    // which the client's 2026-07-24 Figma doesn't do. Long bullet lines
    // (the EMP form names run past 900px) were wrapping early as a result.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Title level={4} style={{ margin: 0, fontWeight: 700, color: '#111827' }}>
        {title}
      </Title>
      {description && (
        <Paragraph style={{ margin: 0, color: '#374151', fontSize: 14 }}>{description}</Paragraph>
      )}
      {bullets.length > 0 && (
        <div>
          {subheader && (
            <Text strong style={{ display: 'block', color: '#111827', marginBottom: 8 }}>
              {subheader}
            </Text>
          )}
          <ul style={{ margin: 0, paddingLeft: 20, color: '#374151', fontSize: 14 }}>
            {bullets.map((line, i) => (
              <li key={i} style={{ marginBottom: 6 }}>
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}
      {showLanguageSelector && (
        <div>
          <Text strong style={{ display: 'block', color: '#111827', marginBottom: 8 }}>
            {t('formRenderer.welcome.selectLanguage')}
          </Text>
          {/* BmoRadioGroup renders the BMO design spec (24px ring, 2px
              #646c76 border, 12px blue dot) — same control the runtime
              RadioGroup field type uses. This step was the last surface
              still on AntD's default Radio, which is why its language
              picker looked different from every other radio in the flow.

              Binds to `pdfLanguage` (the client's chosen PDF locale) — NOT
              `language` (the advisor's UI locale). Picking French reroutes
              the PDF template pick without flipping the advisor's chrome. */}
          <BmoRadioGroup<'en' | 'fr'>
            options={[
              { value: 'en', label: t('formRenderer.welcome.english') },
              { value: 'fr', label: t('formRenderer.welcome.french') },
            ]}
            value={pdfLanguage}
            onChange={onPdfLanguageChange}
            disabled={!onPdfLanguageChange}
          />
          {/* The choice sets the DOCUMENTS' language, not the screen's — say so (RDSP story 139 AC2). */}
          <Text style={{ display: 'block', marginTop: 8, fontSize: 13, color: '#4b5563' }}>
            {t('formRenderer.welcome.languageNote')}
          </Text>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginTop: 8,
          paddingBottom: 16,
          borderBottom: '1px solid #ececee',
        }}
      >
        {onCancel && (
          <Button style={PILL_BUTTON} onClick={onCancel}>
            {t('formRenderer.welcome.cancel')}
          </Button>
        )}
        {onContinue && (
          <Button type="primary" style={PILL_BUTTON} onClick={onContinue}>
            {t('formRenderer.welcome.continue')}
          </Button>
        )}
      </div>
    </div>
  );
}
